import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import PDFViewer from '../components/PDFViewer';
import { 
  FileText, 
  DollarSign, 
  Car, 
  CheckCircle2, 
  History,
  RefreshCw,
  Calendar,
  Edit3,
  Repeat,
  FolderOpen,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import expedienteService from '../services/expedienteService';
import bcvService from '../services/bcvService';
import { getClienteStatusColor, getExpedienteStatusColor } from '../utils/statusColors';
import CustomCalendar from '../components/CustomCalendar';
import NotificationModal from '../components/NotificationModal';
import './ExpedienteDetalle.css';

const ExpedienteDetalle = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isDark } = useTheme();
  const [expediente, setExpediente] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('informacion');
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [statusEnums, setStatusEnums] = useState({ cliente_status: [], expediente_status: [] });
  const [showCalendar, setShowCalendar] = useState(false);
  const [calendarFieldValue, setCalendarFieldValue] = useState('');
  const [notification, setNotification] = useState({ show: false, type: 'success', title: '', message: '' });
  const [originalData, setOriginalData] = useState(null); // Para detectar cambios reales
  const calculatedFinanzasRef = React.useRef({}); // Para almacenar valores calculados

  const canEdit = user?.role === 'admin' || user?.role === 'auditor';

  useEffect(() => {
    loadExpediente();
    loadStatusEnums();
  }, [id]);

  // Sincronizar automáticamente con Strapi al abrir pestaña de Finanzas
  useEffect(() => {
    if (activeTab === 'finanzas' && expediente && !editing) {
      syncStrapiAuto();
    }
  }, [activeTab, expediente?._id]);

  const syncStrapiAuto = async () => {
    try {
      const response = await expedienteService.syncStrapiSingleExpediente(id);
      if (response.updated) {
        console.log('✅ Pagos Strapi actualizados automáticamente:', response);
        // Recargar el expediente para mostrar los valores actualizados
        loadExpediente();
      }
    } catch (error) {
      console.log('ℹ️ No se pudo sincronizar con Strapi:', error);
      // No mostrar error al usuario, es una sincronización silenciosa
    }
  };

  const loadStatusEnums = async () => {
    try {
      const data = await expedienteService.getStatusEnums();
      setStatusEnums(data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const loadExpediente = async () => {
    setLoading(true);
    try {
      const data = await expedienteService.getExpediente(id);
      setExpediente(data);
      setFormData(data);
      setOriginalData(JSON.parse(JSON.stringify(data))); // Copia profunda para comparar
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const getOnlyChangedFields = (original, current) => {
    const changes = {};
    
    // Comparar cada sección
    const sections = ['informacion', 'finanzas', 'producto', 'validacion'];
    
    sections.forEach(section => {
      const originalSection = original[section] || {};
      const currentSection = current[section] || {};
      
      // Verificar si hay cambios en esta sección
      let hasChanges = false;
      for (let key in currentSection) {
        if (originalSection[key] !== currentSection[key]) {
          hasChanges = true;
          break;
        }
      }
      
      if (hasChanges) {
        changes[section] = currentSection;
      }
    });
    
    // Cliente status
    if (original.cliente_status !== current.cliente_status) {
      changes.cliente_status = current.cliente_status;
    }
    
    console.log('🔍 Solo campos cambiados:', Object.keys(changes));
    return changes;
  };

  const captureCalculatedFinanzas = () => {
    // Retornar los valores almacenados en el ref, incluyendo pagos_app_auto
    const values = calculatedFinanzasRef.current || {};
    // Agregar pagos_app_auto al guardar
    if (activeTab === 'finanzas') {
      // Buscar el valor actual de pagos_app_auto del componente
      const pagosAppAutoElement = document.querySelector('[data-testid="pagos-app-auto"]');
      if (pagosAppAutoElement) {
        const text = pagosAppAutoElement.textContent.replace('€', '').replace(',', '.').trim();
        values.pagos_app_auto = parseFloat(text) || 0;
      }
    }
    return values;
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const oldStatus = expediente.expediente_status;
      
      // Si estamos en finanzas, incluir los valores calculados
      let dataToSave = { ...formData };
      if (activeTab === 'finanzas') {
        const calculatedValues = captureCalculatedFinanzas();
        if (calculatedValues.total_pagado !== undefined) {
          dataToSave = {
            ...formData,
            finanzas: {
              ...formData.finanzas,
              total_viejo: calculatedValues.total_viejo,
              total_pagado: calculatedValues.total_pagado,
              porcentaje: calculatedValues.porcentaje
            }
          };
          console.log('📊 Valores calculados a guardar:', calculatedValues);
        }
      }
      
      // Solo enviar campos que realmente cambiaron
      const updateData = getOnlyChangedFields(originalData, dataToSave);
      
      // Convertir campos de moneda de strings con coma a números con punto
      if (updateData.finanzas) {
        const currencyFields = ['valor_cuota', 'pago_bs', 'pago_divisas', 'total_pagado', 'pago_app', 'total_viejo', 'porcentaje', 'monto_penalidad', 'saldo_pendiente'];
        currencyFields.forEach(field => {
          if (updateData.finanzas[field] !== undefined && updateData.finanzas[field] !== null) {
            const value = updateData.finanzas[field];
            // Convertir string con coma a número
            if (typeof value === 'string') {
              const numValue = parseFloat(value.replace(',', '.'));
              updateData.finanzas[field] = isNaN(numValue) ? 0 : numValue;
            }
          }
        });
      }
      
      console.log('📤 Datos a enviar (solo campos modificados, números convertidos):', updateData);
      
      const result = await expedienteService.updateExpediente(id, updateData);
      
      // Verificar si el estado cambió automáticamente
      if (result.expediente_status !== oldStatus) {
        setNotification({
          show: true,
          type: 'success',
          title: 'Cambios Guardados',
          message: `Estado automáticamente actualizado: "${oldStatus}" → "${result.expediente_status}"`
        });
      } else {
        setNotification({
          show: true,
          type: 'success', 
          title: 'Cambios Guardados',
          message: 'Todos los cambios se guardaron exitosamente'
        });
      }
      
      setEditing(false);
      loadExpediente();
      setOriginalData(JSON.parse(JSON.stringify(formData))); // Actualizar datos originales
    } catch (error) {
      console.error('Error:', error);
      setNotification({
        show: true,
        type: 'error',
        title: 'Error al Guardar',
        message: error.response?.data?.detail || 'Error al guardar los cambios'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (group, field, value) => {
    console.log(`💾 Guardando campo: ${group}.${field} = ${value}`);
    setFormData(prev => {
      const newData = {
        ...prev,
        [group]: { ...prev[group], [field]: value },
      };
      
      // Si estamos en finanzas y cambiamos campos relevantes, actualizar valores calculados
      if (group === 'finanzas' && ['pago_bs', 'pago_divisas', 'pago_app', 'plan'].includes(field)) {
        // Los valores calculados se actualizarán cuando se guarde
        // No hacemos nada aquí, solo los capturamos al guardar
      }
      
      return newData;
    });
  };

  const handleStatusChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleOpenCalendar = (fieldKey, currentValue) => {
    setCalendarFieldValue(currentValue || '');
    setShowCalendar(true);
    // Guardar qué campo estamos editando
    setShowCalendar({ field: fieldKey, value: currentValue || '' });
  };

  const handleCalendarSelect = (selectedDate) => {
    if (showCalendar && showCalendar.field) {
      handleInputChange('finanzas', showCalendar.field, selectedDate);
    }
    setShowCalendar(false);
  };

  const closeNotification = () => {
    setNotification({ show: false, type: 'success', title: '', message: '' });
  };

  if (loading) {
    return (
      <div className="detalle-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Cargando expediente...</p>
        </div>
      </div>
    );
  }

  if (!expediente) {
    return (
      <div className="detalle-container">
        <div className="empty-state">
          <p>Expediente no encontrado</p>
          <button onClick={() => navigate('/')} className="back-btn">Volver al Dashboard</button>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'informacion', label: 'Información', icon: <FileText size={18} /> },
    { id: 'finanzas', label: 'Finanzas', icon: <DollarSign size={18} /> },
    { id: 'producto', label: 'Producto', icon: <Car size={18} /> },
    { id: 'validacion', label: 'Validación', icon: <CheckCircle2 size={18} /> },
    { id: 'documentacion', label: 'Documentación', icon: <FolderOpen size={18} /> },
    { id: 'historial', label: 'Historial', icon: <History size={18} /> },
  ];

  return (
    <div className="detalle-container" data-testid="expediente-detalle">
      {/* Header */}
      <div className="detalle-header">
        <button onClick={() => navigate('/')} className="back-link" data-testid="back-button">
          ← Volver al Dashboard
        </button>
        <div className="header-content">
          <div>
            <h1>{formData?.informacion?.nombre} {formData?.informacion?.apellido}</h1>
            <p>Cédula: {formData?.informacion?.cedula} | Contrato: {formData?.informacion?.contrato}</p>
          </div>
          <div className="status-badges">
            <span className={`badge ${getClienteStatusColor(formData?.cliente_status)}`}>
              {formData?.cliente_status}
            </span>
            <span className={`badge ${getExpedienteStatusColor(formData?.expediente_status)}`}>
              {formData?.expediente_status}
            </span>
          </div>
        </div>
      </div>

      {/* Edit Controls */}
      {canEdit && (
        <div className="edit-controls">
          {editing ? (
            <>
              <button onClick={() => { setEditing(false); setFormData(expediente); }} className="btn-cancel" data-testid="cancel-edit-button">
                Cancelar
              </button>
              <button onClick={handleSave} disabled={saving} className="btn-save" data-testid="save-button">
                {saving ? 'Guardando...' : 'Guardar Cambios'}
              </button>
            </>
          ) : (
            <button onClick={() => setEditing(true)} className="btn-edit" data-testid="edit-button">
              Editar Expediente
            </button>
          )}
        </div>
      )}

      {/* Status Edit */}
      {editing && (
        <div className="status-info-card">
          <h3>Estado del Expediente</h3>
          <div className="status-info">
            <span className={`badge ${getExpedienteStatusColor(formData?.expediente_status)}`}>
              {formData?.expediente_status}
            </span>
            <p className="status-note">
              ℹ️ El estado del expediente se actualiza automáticamente según las modificaciones realizadas.
            </p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="tabs-card">
        <div className="tabs-header">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`tab-btn ${activeTab === tab.id ? 'tab-btn-active' : ''}`}
              data-testid={`tab-${tab.id}`}
            >
              <span>{tab.icon}</span>
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        <div className="tab-content">
          {activeTab === 'informacion' && (
            <InformacionTab 
              data={formData.informacion} 
              editing={editing} 
              onChange={handleInputChange}
              clienteStatus={formData.cliente_status}
              onClienteStatusChange={(value) => handleStatusChange('cliente_status', value)}
              statusEnums={statusEnums}
            />
          )}
          {activeTab === 'finanzas' && <FinanzasTab data={formData.finanzas} editing={editing} onChange={handleInputChange} onOpenCalendar={handleOpenCalendar} calculatedValuesRef={calculatedFinanzasRef} expedienteId={id} />}
          {activeTab === 'producto' && <ProductoTab data={formData.producto} editing={editing} onChange={handleInputChange} />}
          {activeTab === 'validacion' && <ValidacionTab data={formData.validacion} editing={editing} onChange={handleInputChange} />}
          {activeTab === 'documentacion' && <DocumentacionTab cedula={formData?.informacion?.cedula} />}
          {activeTab === 'historial' && <HistorialTab history={expediente.audit_history || []} />}
        </div>
      </div>

      {/* Calendario Personalizado */}
      <CustomCalendar
        isOpen={!!showCalendar}
        onClose={() => setShowCalendar(false)}
        onDateSelect={handleCalendarSelect}
        selectedDate={showCalendar.value}
        theme={isDark ? 'dark' : 'light'}
      />

      {/* Notificación Personalizada */}
      <NotificationModal
        isOpen={notification.show}
        type={notification.type}
        title={notification.title}
        message={notification.message}
        onClose={closeNotification}
      />
    </div>
  );
};

const InformacionTab = ({ data, editing, onChange, clienteStatus, onClienteStatusChange, statusEnums }) => {
  const fields = [
    { key: 'sede', label: 'Sede' },
    { key: 'nombre', label: 'Nombre' },
    { key: 'apellido', label: 'Apellido' },
    { key: 'contrato', label: 'Contrato' },
    { key: 'cedula', label: 'Cédula' },
    { key: 'telefono', label: 'Teléfono' },
    { key: 'email', label: 'Correo Electrónico' },
    { key: 'modelo_contrato', label: 'Modelo Contrato' },
    { key: 'status', label: 'Status' },
    { key: 'fecha_inscripcion', label: 'Fecha de Inscripción', type: 'text' },  // NUEVO CAMPO
  ];

  return (
    <div className="tab-content">
      <div className="fields-grid">
        {fields.map((field) => (
          <div key={field.key} className="field-item">
            <label>{field.label}</label>
            {editing ? (
              <input type="text" value={data?.[field.key] || ''} onChange={(e) => onChange('informacion', field.key, e.target.value)} data-testid={`input-${field.key}`} />
            ) : (
              <p>{data?.[field.key] || '-'}</p>
            )}
          </div>
        ))}
        
        {/* Campo Cliente Status - Select en Información */}
        <div className="field-item">
          <label>Estatus Cliente</label>
          {editing ? (
            <select 
              value={clienteStatus || 'Activo'} 
              onChange={(e) => onClienteStatusChange(e.target.value)} 
              data-testid="cliente-status-select"
              className="status-select"
            >
              {statusEnums.cliente_status.map(status => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
          ) : (
            <div className="status-display">
              <span className={`badge ${getClienteStatusColor(clienteStatus)}`}>
                {clienteStatus}
              </span>
            </div>
          )}
        </div>
      </div>
      
      {/* Campo Observaciones - Ancho completo */}
      <div className="field-item full-width" style={{ marginTop: '24px' }}>
        <label>Observaciones</label>
        {editing ? (
          <textarea 
            value={data?.observaciones || ''} 
            onChange={(e) => onChange('informacion', 'observaciones', e.target.value)} 
            data-testid="input-observaciones"
            rows="4"
            placeholder="Agregar observaciones sobre el cliente..."
            style={{
              width: '100%',
              padding: '12px 16px',
              borderRadius: '12px',
              fontSize: '14px',
              resize: 'vertical',
              minHeight: '100px'
            }}
          />
        ) : (
          <p style={{ whiteSpace: 'pre-wrap' }}>{data?.observaciones || '-'}</p>
        )}
      </div>
    </div>
  );
};

const FinanzasTab = ({ data, editing, onChange, onOpenCalendar, calculatedValuesRef, expedienteId }) => {
  const [bcvRates, setBcvRates] = useState({ USD: 250, EUR: 280 }); // Tasas de fallback iniciales
  const [loadingRates, setLoadingRates] = useState(false);
  const [ratesStatus, setRatesStatus] = useState('fallback'); // 'live', 'fallback', 'error'
  const [pagosBsAjustado, setPagosBsAjustado] = useState(0); // Campo calculado
  const [pagosAppAuto, setPagosAppAuto] = useState(0); // Pagos de Strapi
  const [ultimoPagoApp, setUltimoPagoApp] = useState(null); // Fecha último pago app
  const [pagosOficina, setPagosOficina] = useState(0); // Pagos de oficina
  const [ultimoPagoOficina, setUltimoPagoOficina] = useState(null); // Fecha último pago oficina
  const [loadingStrapi, setLoadingStrapi] = useState(false);
  const [loadingOficina, setLoadingOficina] = useState(false);
  
  // Estados para modales de conversión de moneda
  const [showPenalidadModal, setShowPenalidadModal] = useState(false);
  const [showSaldoModal, setShowSaldoModal] = useState(false);
  const [modalMoneda, setModalMoneda] = useState('EUR');
  const [modalMonto, setModalMonto] = useState('');

  // Cargar tasas BCV al montar el componente
  useEffect(() => {
    loadBCVRates();
    loadPagosStrapi();
    loadPagosOficina();
    
    // Cargar fecha del último pago si ya está en DB
    if (data?.ultimo_pago_app) {
      setUltimoPagoApp(data.ultimo_pago_app);
    }
    if (data?.ultimo_pago_oficina) {
      setUltimoPagoOficina(data.ultimo_pago_oficina);
    }
  }, []);

  // Función para cargar pagos de Strapi
  const loadPagosStrapi = async () => {
    if (!expedienteId) return;
    
    setLoadingStrapi(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/expedientes/${expedienteId}/pagos-strapi`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const result = await response.json();
        setPagosAppAuto(result.total_pagos_strapi || 0);
        setUltimoPagoApp(result.fecha_ultimo_pago || null);
        console.log(`✅ Pagos Strapi cargados: €${result.total_pagos_strapi}, último pago: ${result.fecha_ultimo_pago}`);
      }
    } catch (error) {
      console.error('Error cargando pagos Strapi:', error);
    } finally {
      setLoadingStrapi(false);
    }
  };

  // Función para cargar pagos de oficina
  const loadPagosOficina = async () => {
    if (!expedienteId) return;
    
    setLoadingOficina(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/expedientes/${expedienteId}/pagos-oficina`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const result = await response.json();
        setPagosOficina(result.total_pagos_oficina || 0);
        setUltimoPagoOficina(result.fecha_ultimo_pago || null);
        console.log(`✅ Pagos Oficina cargados: €${result.total_pagos_oficina}, último pago: ${result.fecha_ultimo_pago}`);
      }
    } catch (error) {
      console.error('Error cargando pagos oficina:', error);
    } finally {
      setLoadingOficina(false);
    }
  };

  // Calcular ajuste cuando cambien los datos
  useEffect(() => {
    if (data && data.pago_bs && data.fecha_ultimo_pago) {
      calcularPagosBsAjustado();
    }
  }, [data, bcvRates]);

  const calcularPagosBsAjustado = async () => {
    const pagosBsUsd = parseFloat(parseCurrency(data?.pago_bs || '0'));
    const fechaUltimoPago = data?.fecha_ultimo_pago;
    
    const ajustado = await calculatePagosBsAjustado(pagosBsUsd, fechaUltimoPago);
    setPagosBsAjustado(ajustado);
  };

  const loadBCVRates = async () => {
    setLoadingRates(true);
    try {
      const rates = await bcvService.getRates();
      if (rates.success && rates.data && rates.data.USD > 0) {
        setBcvRates({
          USD: rates.data.USD,
          EUR: rates.data.EUR || 0
        });
        setRatesStatus('live');
      } else {
        setBcvRates({ USD: 250, EUR: 280 });
        setRatesStatus('fallback');
      }
    } catch (error) {
      console.error('Error cargando tasas BCV:', error);
      setBcvRates({ USD: 250, EUR: 280 });
      setRatesStatus('error');
    } finally {
      setLoadingRates(false);
    }
  };

  // Funciones para manejar modales de conversión de moneda
  const convertirMoneda = (monto, moneda) => {
    const valor = parseFloat(monto);
    if (!valor || isNaN(valor)) return 0;
    
    if (moneda === 'EUR') return valor;
    if (moneda === 'USD') {
      // USD a EUR: (valor USD * tasa USD en Bs) / tasa EUR en Bs
      return (valor * bcvRates.USD) / bcvRates.EUR;
    }
    if (moneda === 'BS') {
      // Bs a EUR: valor Bs / tasa EUR en Bs
      return valor / bcvRates.EUR;
    }
    return 0;
  };

  const handleOpenPenalidadModal = () => {
    setModalMoneda('EUR');
    setModalMonto('');
    setShowPenalidadModal(true);
  };

  const handleOpenSaldoModal = () => {
    setModalMoneda('EUR');
    setModalMonto('');
    setShowSaldoModal(true);
  };

  const handleSavePenalidad = () => {
    const montofloat = convertirMoneda(modalMonto, modalMoneda);
    onChange('finanzas', 'monto_penalidad', montofloat.toFixed(2));
    setShowPenalidadModal(false);
    setModalMonto('');
  };

  const handleSaveSaldo = () => {
    const eurValue = convertirMoneda(modalMonto, modalMoneda);
    onChange('finanzas', 'saldo_pendiente', eurValue.toFixed(2));
    setShowSaldoModal(false);
    setModalMonto('');
  };

  // Campos manuales (editables) - SIN penalidad y saldo (tienen su propio modal)
  const manualFields = [
    { key: 'valor_cuota', label: 'Valor Cuota', type: 'currency' },
    { key: 'cantidad_cuota', label: 'Cantidad Cuota', type: 'integer' },
    { key: 'pago_bs', label: 'Pagos Bs (USD)', type: 'currency' },        // USD manual
    { key: 'pago_divisas', label: 'Pago Divisas (USD)', type: 'currency' }, // USD manual
    { key: 'pago_app', label: 'Pagos App (EUR)', type: 'currency' },      // EUR manual - CORREGIDO
    { key: 'fecha_ultimo_pago', label: 'Fecha Último Pago', type: 'date' },
    { key: 'meses_impago', label: 'Meses Impago', type: 'integer' },      // NUEVO - manual simple
  ];

  // Generar opciones de plan (800 a 5000, de 100 en 100)
  const planOptions = [];
  for (let i = 800; i <= 5000; i += 100) {
    planOptions.push(i);
  }

  const formatCurrency = (value) => {
    if (!value && value !== 0) return '';
    return value.toString().replace('.', ',');
  };

  const parseCurrency = (value) => {
    if (!value && value !== 0) return '0';
    // Asegurar que sea string antes de replace
    const stringValue = typeof value === 'string' ? value : String(value);
    return stringValue.replace(',', '.');
  };

  const formatDate = (dateValue) => {
    if (!dateValue) return '';
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateValue)) {
      return dateValue;
    }
    const date = new Date(dateValue);
    if (!isNaN(date.getTime())) {
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    }
    return dateValue;
  };

  const parseDate = (dateStr) => {
    if (!dateStr) return '';
    const ddmmyyyy = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(dateStr);
    if (ddmmyyyy) {
      const [, day, month, year] = ddmmyyyy;
      return `${year}-${month}-${day}`;
    }
    return dateStr;
  };

  const handleDateChange = (field, value) => {
    if (value) {
      const date = new Date(value);
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      const formattedDate = `${day}/${month}/${year}`;
      onChange('finanzas', field, formattedDate);
    } else {
      onChange('finanzas', field, '');
    }
  };

  const handleManualDateChange = (field, value) => {
    let cleanValue = value.replace(/[^\d/]/g, '');
    
    if (cleanValue.length >= 2 && cleanValue.charAt(2) !== '/') {
      cleanValue = cleanValue.substring(0, 2) + '/' + cleanValue.substring(2);
    }
    if (cleanValue.length >= 5 && cleanValue.charAt(5) !== '/') {
      cleanValue = cleanValue.substring(0, 5) + '/' + cleanValue.substring(5);
    }
    
    if (cleanValue.length > 10) {
      cleanValue = cleanValue.substring(0, 10);
    }
    
    onChange('finanzas', field, cleanValue);
  };

  const handleCurrencyChange = (field, value) => {
    const cleanValue = value.replace(/[^\d,]/g, '');
    
    const parts = cleanValue.split(',');
    if (parts.length > 2) return;
    if (parts[1] && parts[1].length > 2) return;
    
    onChange('finanzas', field, cleanValue);
  };

  const handleIntegerChange = (field, value) => {
    const cleanValue = value.replace(/[^\d]/g, '');
    onChange('finanzas', field, cleanValue);
  };

  // Función para calcular Pagos Bs Ajustado (con tasas históricas)
  const calculatePagosBsAjustado = async (pagosBsUsd, fechaUltimoPago) => {
    if (!pagosBsUsd || pagosBsUsd <= 0 || !fechaUltimoPago) {
      return parseFloat(pagosBsUsd) || 0; // Si no hay datos, retornar original como número
    }

    try {
      // Parsear fecha último pago (DD/MM/AAAA → Date)
      const dateParts = fechaUltimoPago.split('/');
      if (dateParts.length !== 3) return parseFloat(pagosBsUsd);
      
      const fechaPago = new Date(dateParts[2], dateParts[1] - 1, dateParts[0]);
      const fecha30Dias = new Date(fechaPago);
      fecha30Dias.setDate(fecha30Dias.getDate() + 30);
      
      const hoy = new Date();
      
      console.log(`Fecha pago: ${fechaPago.toISOString().split('T')[0]}`);
      console.log(`Fecha + 30: ${fecha30Dias.toISOString().split('T')[0]}`);
      console.log(`Hoy: ${hoy.toISOString().split('T')[0]}`);
      
      // Si fecha + 30 días >= hoy, usar mismo monto
      if (fecha30Dias >= hoy) {
        console.log('✅ No aplica ajuste (fecha reciente), usando valor original');
        return parseFloat(pagosBsUsd);
      }
      
      // Si han pasado más de 30 días, aplicar fórmula de ajuste
      const fechaHistorica = fecha30Dias.toISOString().split('T')[0]; // YYYY-MM-DD
      console.log(`Obteniendo tasa histórica para: ${fechaHistorica}`);
      
      const tasaHistorica = await bcvService.getHistoricalRate('USD', fechaHistorica);
      
      if (tasaHistorica.success && tasaHistorica.data && tasaHistorica.data.rate) {
        const tasaActual = bcvRates.USD || 241.578;
        const tasa30Dias = tasaHistorica.data.rate;
        
        // Fórmula CORRECTA: (monto Pagos Bs × tasa USD VES fecha+30) ÷ tasa_actual
        const ajustado = (parseFloat(pagosBsUsd) * tasa30Dias) / tasaActual;
        
        console.log(`🔢 Fórmula corregida: (${pagosBsUsd} × ${tasa30Dias}) ÷ ${tasaActual} = ${ajustado}`);
        return ajustado;
      } else {
        console.log('❌ No se pudo obtener tasa histórica, usando valor original');
        return parseFloat(pagosBsUsd);
      }
      
    } catch (error) {
      console.error('Error calculando ajuste:', error);
      return parseFloat(pagosBsUsd);
    }
  };

  const calculateValues = (currentData) => {
    // Si no estamos editando, usar los valores guardados en DB directamente
    // total_pagado en DB ya incluye pagos_app_auto y pagos_oficina
    if (!editing && currentData?.total_pagado !== undefined) {
      return {
        total_viejo: currentData.total_viejo || '0',
        total_pagado: currentData.total_pagado || '0',
        porcentaje: currentData.porcentaje || '0'
      };
    }
    
    // Solo calcular si estamos editando
    const pago_divisas_usd = parseFloat(parseCurrency(currentData?.pago_divisas || '0')); // USD manual  
    const pago_app_eur = parseFloat(parseCurrency(currentData?.pago_app || '0'));      // EUR manual
    const plan = parseInt(currentData?.plan || '0');

    // 1. Total USD = Pagos Bs Ajustado + Pago Divisas (ambos en USD)
    const total_usd = pagosBsAjustado + pago_divisas_usd;
    
    // Conversiones con tasas exactas del BCV
    const usd_rate = bcvRates.USD || 241.578; // VES por 1 USD
    const eur_rate = bcvRates.EUR || 278.428; // VES por 1 EUR
    
    // 2. Total Viejo = (Total USD convertido a EUR) + Pagos App
    const total_usd_en_eur = (total_usd * usd_rate) / eur_rate;
    const total_viejo_eur = total_usd_en_eur + pago_app_eur;
    
    // 3. Total Pagado = Total Viejo + Pagos App Auto + Pagos Oficina
    const total_pagado_eur = total_viejo_eur + pagosAppAuto + pagosOficina;
    
    // 3. Plan + 25% para cálculo de porcentaje
    const plan_con_incremento = plan * 1.25;
    
    // 4. Porcentaje = Total Pagado / (Plan + 25%) * 100
    const porcentaje_avance = plan_con_incremento > 0 ? (total_pagado_eur / plan_con_incremento) * 100 : 0;

    return {
      total_viejo: total_viejo_eur.toFixed(2),     // EUR
      total_pagado: total_pagado_eur.toFixed(2),   // EUR (Total Viejo + Pagos App Auto + Pagos Oficina)
      porcentaje: porcentaje_avance.toFixed(2)     // Porcentaje
    };
  };

  const calculatedValues = data ? calculateValues(data) : {};

  // Actualizar el ref con los valores calculados
  React.useEffect(() => {
    if (calculatedValuesRef && calculatedValues) {
      calculatedValuesRef.current = {
        total_viejo: parseFloat(calculatedValues.total_viejo) || 0,
        total_pagado: parseFloat(calculatedValues.total_pagado) || 0,
        porcentaje: parseFloat(calculatedValues.porcentaje) || 0,
        pagos_app_auto: pagosAppAuto,
        pagos_oficina: pagosOficina
      };
    }
  }, [calculatedValues.total_viejo, calculatedValues.total_pagado, calculatedValues.porcentaje, pagosAppAuto, pagosOficina, calculatedValuesRef]);

  return (
    <div className="finanzas-container">
      {/* Tasas BCV Header */}
      <div className="bcv-rates-header">
        <div className="rates-info">
          <h4>💱 Tasas BCV 
            {ratesStatus === 'live' && <span className="status-live">🟢 En vivo</span>}
            {ratesStatus === 'fallback' && <span className="status-fallback">🟡 Estimadas</span>}
            {ratesStatus === 'error' && <span className="status-error">🔴 Error</span>}
          </h4>
          <div className="rates-display">
            <span className="rate-item">
              USD: {bcvRates.USD?.toString().replace('.', ',') || '250,00'} Bs
            </span>
            <span className="rate-item">
              EUR: {bcvRates.EUR?.toFixed(2).replace('.', ',') || '277,51'} Bs  
            </span>
            <button onClick={loadBCVRates} className="refresh-rates" disabled={loadingRates}>
              {loadingRates ? <RefreshCw size={14} className="spinning" /> : <RefreshCw size={14} />}
            </button>
          </div>
        </div>
      </div>

      <div className="finanzas-section">
        <h4 className="section-title">
          <Edit3 size={18} style={{ display: 'inline-block', marginRight: '8px', verticalAlign: 'middle' }} />
          Campos Manuales
        </h4>
        <div className="fields-grid">
          {manualFields.map((field) => (
            <div key={field.key} className="field-item">
              <label>{field.label}</label>
              {editing ? (
                field.type === 'currency' ? (
                  <input
                    type="text"
                    value={formatCurrency(data?.[field.key] || '')}
                    onChange={(e) => handleCurrencyChange(field.key, e.target.value)}
                    placeholder="0,00"
                    className="currency-input"
                    data-testid={`input-${field.key}`}
                  />
                ) : field.type === 'integer' ? (
                  <input
                    type="text"
                    value={data?.[field.key] || ''}
                    onChange={(e) => handleIntegerChange(field.key, e.target.value)}
                    placeholder="0"
                    className="integer-input"
                    data-testid={`input-${field.key}`}
                  />
                ) : field.key === 'fecha_ultimo_pago' ? (
                  <div className="date-input-container">
                    <input
                      type="text"
                      value={data?.[field.key] || ''}
                      onChange={(e) => handleManualDateChange(field.key, e.target.value)}
                      placeholder="DD/MM/AAAA"
                      className="date-manual-input"
                      data-testid={`input-${field.key}-manual`}
                    />
                    <button
                      type="button"
                      onClick={() => onOpenCalendar(field.key, data?.[field.key])}
                      className="calendar-button"
                      data-testid={`calendar-${field.key}`}
                    >
                      <Calendar size={16} />
                    </button>
                  </div>
                ) : (
                  <input
                    type="text"
                    value={data?.[field.key] || ''}
                    onChange={(e) => onChange('finanzas', field.key, e.target.value)}
                    data-testid={`input-${field.key}`}
                  />
                )
              ) : (
                <p className="field-value">{field.key === 'fecha_ultimo_pago' ? (data?.[field.key] || '-') : (formatCurrency(data?.[field.key]) || '-')}</p>
              )}
            </div>
          ))}

          {/* Campo Plan - Select */}
          <div className="field-item">
            <label>Plan</label>
            {editing ? (
              <select
                value={data?.plan || ''}
                onChange={(e) => onChange('finanzas', 'plan', e.target.value)}
                className="plan-select"
                data-testid="input-plan"
              >
                <option value="">Seleccionar Plan</option>
                {planOptions.map(plan => (
                  <option key={plan} value={plan}>{plan.toLocaleString()}</option>
                ))}
              </select>
            ) : (
              <p className="field-value">{data?.plan ? parseInt(data.plan).toLocaleString() : '-'}</p>
            )}
          </div>

          {/* Campo Monto Penalidad EUR - Con modal de conversión */}
          <div className="field-item">
            <label>Monto Penalidad (EUR)</label>
            {editing ? (
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input
                  type="text"
                  value={formatCurrency(data?.monto_penalidad || '')}
                  readOnly
                  placeholder="0,00"
                  style={{
                    flex: 1,
                    padding: '12px 16px',
                    borderRadius: '12px',
                    fontSize: '14px',
                    background: 'rgba(55, 65, 81, 0.3)',
                    border: '1px solid rgba(75, 85, 99, 0.5)',
                    color: 'white',
                    cursor: 'not-allowed'
                  }}
                />
                <button
                  type="button"
                  onClick={handleOpenPenalidadModal}
                  style={{
                    padding: '12px 16px',
                    background: '#fbb040',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#111827',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap'
                  }}
                >
                  + Agregar
                </button>
              </div>
            ) : (
              <p className="field-value">€ {formatCurrency(data?.monto_penalidad) || '-'}</p>
            )}
          </div>

          {/* Campo Saldo Pendiente EUR - Con modal de conversión */}
          <div className="field-item">
            <label>Saldo Pendiente (EUR)</label>
            {editing ? (
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input
                  type="text"
                  value={formatCurrency(data?.saldo_pendiente || '')}
                  readOnly
                  placeholder="0,00"
                  style={{
                    flex: 1,
                    padding: '12px 16px',
                    borderRadius: '12px',
                    fontSize: '14px',
                    background: 'rgba(55, 65, 81, 0.3)',
                    border: '1px solid rgba(75, 85, 99, 0.5)',
                    color: 'white',
                    cursor: 'not-allowed'
                  }}
                />
                <button
                  type="button"
                  onClick={handleOpenSaldoModal}
                  style={{
                    padding: '12px 16px',
                    background: '#fbb040',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#111827',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap'
                  }}
                >
                  + Agregar
                </button>
              </div>
            ) : (
              <p className="field-value">€ {formatCurrency(data?.saldo_pendiente) || '-'}</p>
            )}
          </div>

          {/* Campo Pagos Bs Ajustado - Calculado */}
          <div className="field-item calculated-field">
            <label>Pagos Bs Ajustado (USD)</label>
            <div className="calculated-value">
              <span className="calculated-number">$ {formatCurrency(pagosBsAjustado.toFixed(2))}</span>
              <span className="calculated-label">
                {pagosBsAjustado === parseFloat(parseCurrency(data?.pago_bs || '0')) 
                  ? 'Sin ajuste (fecha reciente)' 
                  : 'Ajustado por devaluación'}
              </span>
            </div>
          </div>

          {/* Campo Pagos App Auto - Desde Strapi */}
          <div className="field-item calculated-field">
            <label>
              Pagos app auto (EUR)
              {editing && (
                <button 
                  onClick={loadPagosStrapi} 
                  className="refresh-strapi-btn"
                  disabled={loadingStrapi}
                  title="Actualizar pagos desde Strapi"
                  style={{
                    marginLeft: '8px',
                    padding: '4px 8px',
                    background: 'rgba(251, 176, 64, 0.2)',
                    border: '1px solid rgba(251, 176, 64, 0.4)',
                    borderRadius: '6px',
                    color: '#fbb040',
                    cursor: loadingStrapi ? 'not-allowed' : 'pointer',
                    fontSize: '12px',
                    transition: 'all 0.2s'
                  }}
                >
                  {loadingStrapi ? <RefreshCw size={12} className="spinning" /> : <RefreshCw size={12} />}
                </button>
              )}
            </label>
            <div className="calculated-value">
              <span className="calculated-number" data-testid="pagos-app-auto">
                {loadingStrapi ? '⏳ Cargando...' : `€ ${formatCurrency(pagosAppAuto.toFixed(2))}`}
              </span>
              <span className="calculated-label">Desde Strapi (confirmados desde En Proceso 2)</span>
            </div>
          </div>

          {/* Campo Último Pago App - Fecha desde Strapi */}
          <div className="field-item calculated-field">
            <label>Último pago App</label>
            <div className="calculated-value">
              <span className="calculated-number">
                {loadingStrapi ? '⏳ Cargando...' : (ultimoPagoApp || '-')}
              </span>
              <span className="calculated-label">Fecha del último pago en Strapi</span>
            </div>
          </div>

          {/* Campo Pagos Oficina - Solo cuota, inicial, finiquito */}
          <div className="field-item calculated-field">
            <label>
              Pagos oficina (EUR)
              {editing && (
                <button 
                  onClick={loadPagosOficina} 
                  className="refresh-strapi-btn"
                  disabled={loadingOficina}
                  title="Actualizar pagos de oficina"
                  style={{
                    marginLeft: '8px',
                    padding: '4px 8px',
                    background: 'rgba(251, 176, 64, 0.2)',
                    border: '1px solid rgba(251, 176, 64, 0.4)',
                    borderRadius: '6px',
                    color: '#fbb040',
                    cursor: loadingOficina ? 'not-allowed' : 'pointer',
                    fontSize: '12px',
                    transition: 'all 0.2s'
                  }}
                >
                  {loadingOficina ? <RefreshCw size={12} className="spinning" /> : <RefreshCw size={12} />}
                </button>
              )}
            </label>
            <div className="calculated-value">
              <span className="calculated-number" data-testid="pagos-oficina">
                {loadingOficina ? '⏳ Cargando...' : `€ ${formatCurrency(pagosOficina.toFixed(2))}`}
              </span>
              <span className="calculated-label">Solo cuota, inicial y finiquito</span>
            </div>
          </div>

          {/* Campo Último Pago Oficina - Fecha */}
          <div className="field-item calculated-field">
            <label>Último pago Oficina</label>
            <div className="calculated-value">
              <span className="calculated-number">
                {loadingOficina ? '⏳ Cargando...' : (ultimoPagoOficina || '-')}
              </span>
              <span className="calculated-label">Fecha del último pago de oficina</span>
            </div>
          </div>

          {/* Campo Total USD - Suma de USD */}
          <div className="field-item calculated-field">
            <label>Total USD</label>
            <div className="calculated-value">
              <span className="calculated-number">$ {formatCurrency((pagosBsAjustado + parseFloat(parseCurrency(data?.pago_divisas || '0'))).toFixed(2))}</span>
              <span className="calculated-label">Pagos Bs Ajustado + Pago Divisas (solo USD)</span>
            </div>
          </div>
        </div>
      </div>

      <div className="finanzas-section">
        <h4 className="section-title">
          <DollarSign size={18} style={{ display: 'inline-block', marginRight: '8px', verticalAlign: 'middle' }} />
          Campos Calculados (BCV)
        </h4>
        <div className="fields-grid">
          <div className="field-item calculated-field">
            <label>Total Viejo (EUR)</label>
            <div className="calculated-value">
              <span className="calculated-number">€ {formatCurrency(calculatedValues.total_viejo || '0')}</span>
              <span className="calculated-label">(Total USD → EUR) + Pagos App</span>
            </div>
          </div>

          <div className="field-item calculated-field">
            <label>Total Pagado (EUR)</label>
            <div className="calculated-value">
              <span className="calculated-number">€ {formatCurrency(calculatedValues.total_pagado || '0')}</span>
              <span className="calculated-label">Total Viejo + Pagos App Auto + Pagos Oficina</span>
            </div>
          </div>

          <div className="field-item calculated-field">
            <label>Porcentaje de Avance (%)</label>
            <div className="calculated-value">
              <span className="calculated-number">{formatCurrency(calculatedValues.porcentaje || '0')}%</span>
              <span className="calculated-label">Total Pagado / (Plan + 25%)</span>
            </div>
          </div>
        </div>
      </div>

      {/* Modal para Monto Penalidad */}
      {showPenalidadModal && (
        <div className="custom-modal-overlay" onClick={() => setShowPenalidadModal(false)}>
          <div className="custom-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="custom-modal-header">
              <h3>Agregar Monto Penalidad</h3>
              <button onClick={() => setShowPenalidadModal(false)} className="modal-close">×</button>
            </div>
            <div className="custom-modal-body">
              <div className="form-group">
                <label>Moneda</label>
                <select 
                  value={modalMoneda} 
                  onChange={(e) => setModalMoneda(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '8px',
                    border: '1px solid rgba(75, 85, 99, 0.5)',
                    background: 'rgba(55, 65, 81, 0.5)',
                    color: 'white',
                    fontSize: '14px'
                  }}
                >
                  <option value="EUR">EUR (€)</option>
                  <option value="USD">USD ($)</option>
                  <option value="BS">Bolívares (Bs)</option>
                </select>
              </div>
              <div className="form-group">
                <label>Monto</label>
                <input
                  type="number"
                  step="0.01"
                  value={modalMonto}
                  onChange={(e) => setModalMonto(e.target.value)}
                  placeholder="0.00"
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '8px',
                    border: '1px solid rgba(75, 85, 99, 0.5)',
                    background: 'rgba(55, 65, 81, 0.5)',
                    color: 'white',
                    fontSize: '14px'
                  }}
                />
              </div>
              {modalMoneda !== 'EUR' && modalMonto && (
                <div className="conversion-info" style={{ 
                  padding: '12px', 
                  background: 'rgba(251, 176, 64, 0.1)', 
                  borderRadius: '8px',
                  marginTop: '12px'
                }}>
                  <p style={{ margin: 0, fontSize: '13px', color: '#9ca3af' }}>
                    Equivalente en EUR: <strong style={{ color: '#fbb040' }}>
                      € {convertirMoneda(modalMonto, modalMoneda).toFixed(2)}
                    </strong>
                  </p>
                  <p style={{ margin: '4px 0 0 0', fontSize: '11px', color: '#6b7280' }}>
                    Tasa {modalMoneda}: {modalMoneda === 'USD' ? bcvRates.USD : bcvRates.EUR} Bs
                  </p>
                </div>
              )}
            </div>
            <div className="custom-modal-footer">
              <button onClick={() => setShowPenalidadModal(false)} className="btn-cancel">
                Cancelar
              </button>
              <button 
                onClick={handleSavePenalidad} 
                className="btn-save"
                disabled={!modalMonto}
              >
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal para Saldo Pendiente */}
      {showSaldoModal && (
        <div className="custom-modal-overlay" onClick={() => setShowSaldoModal(false)}>
          <div className="custom-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="custom-modal-header">
              <h3>Agregar Saldo Pendiente</h3>
              <button onClick={() => setShowSaldoModal(false)} className="modal-close">×</button>
            </div>
            <div className="custom-modal-body">
              <div className="form-group">
                <label>Moneda</label>
                <select 
                  value={modalMoneda} 
                  onChange={(e) => setModalMoneda(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '8px',
                    border: '1px solid rgba(75, 85, 99, 0.5)',
                    background: 'rgba(55, 65, 81, 0.5)',
                    color: 'white',
                    fontSize: '14px'
                  }}
                >
                  <option value="EUR">EUR (€)</option>
                  <option value="USD">USD ($)</option>
                  <option value="BS">Bolívares (Bs)</option>
                </select>
              </div>
              <div className="form-group">
                <label>Monto</label>
                <input
                  type="number"
                  step="0.01"
                  value={modalMonto}
                  onChange={(e) => setModalMonto(e.target.value)}
                  placeholder="0.00"
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '8px',
                    border: '1px solid rgba(75, 85, 99, 0.5)',
                    background: 'rgba(55, 65, 81, 0.5)',
                    color: 'white',
                    fontSize: '14px'
                  }}
                />
              </div>
              {modalMoneda !== 'EUR' && modalMonto && (
                <div className="conversion-info" style={{ 
                  padding: '12px', 
                  background: 'rgba(251, 176, 64, 0.1)', 
                  borderRadius: '8px',
                  marginTop: '12px'
                }}>
                  <p style={{ margin: 0, fontSize: '13px', color: '#9ca3af' }}>
                    Equivalente en EUR: <strong style={{ color: '#fbb040' }}>
                      € {convertirMoneda(modalMonto, modalMoneda).toFixed(2)}
                    </strong>
                  </p>
                  <p style={{ margin: '4px 0 0 0', fontSize: '11px', color: '#6b7280' }}>
                    Tasa {modalMoneda}: {modalMoneda === 'USD' ? bcvRates.USD : bcvRates.EUR} Bs
                  </p>
                </div>
              )}
            </div>
            <div className="custom-modal-footer">
              <button onClick={() => setShowSaldoModal(false)} className="btn-cancel">
                Cancelar
              </button>
              <button 
                onClick={handleSaveSaldo} 
                className="btn-save"
                disabled={!modalMonto}
              >
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ProductoTab = ({ data, editing, onChange }) => {
  const textFields = [
    { key: 'modelo', label: 'Modelo', type: 'text' },
    { key: 'placa', label: 'Placa', type: 'text' },
    { key: 'mes_entrega', label: 'Mes de Entrega', type: 'text' },
    { key: 'anio_entrega', label: 'Año de Entrega', type: 'number' },
    { key: 'pago_seguro', label: 'Pago de Seguro', type: 'number' },
  ];

  const selectFields = [
    { key: 'certificado', label: 'Certificado' },
    { key: 'factura', label: 'Factura' },
    { key: 'cesion_derecho', label: 'Cesión de Derecho' },
  ];

  return (
    <div className="tab-content">
      <div className="fields-grid">
        {/* Campos de texto */}
        {textFields.map((field) => (
          <div key={field.key} className="field-item">
            <label>{field.label}</label>
            {editing ? (
              <input 
                type={field.type} 
                value={data?.[field.key] || ''} 
                onChange={(e) => onChange('producto', field.key, field.type === 'number' ? parseFloat(e.target.value) || '' : e.target.value)} 
                data-testid={`input-${field.key}`} 
              />
            ) : (
              <p>{data?.[field.key] || '-'}</p>
            )}
          </div>
        ))}
        
        {/* Campos de selección SI/NO */}
        {selectFields.map((field) => (
          <div key={field.key} className="field-item">
            <label>{field.label}</label>
            {editing ? (
              <select 
                value={data?.[field.key] || ''} 
                onChange={(e) => onChange('producto', field.key, e.target.value)} 
                data-testid={`select-${field.key}`}
                className="status-select"
              >
                <option value="">Seleccionar...</option>
                <option value="SI">SI</option>
                <option value="NO">NO</option>
              </select>
            ) : (
              <p>{data?.[field.key] || '-'}</p>
            )}
          </div>
        ))}
      </div>
      
      {/* Campo Observaciones - Ancho completo */}
      <div className="field-item full-width" style={{ marginTop: '24px' }}>
        <label>Observaciones</label>
        {editing ? (
          <textarea 
            value={data?.observaciones || ''} 
            onChange={(e) => onChange('producto', 'observaciones', e.target.value)} 
            data-testid="input-producto-observaciones"
            rows="4"
            placeholder="Agregar observaciones sobre el producto..."
            style={{
              width: '100%',
              padding: '12px 16px',
              borderRadius: '12px',
              fontSize: '14px',
              resize: 'vertical',
              minHeight: '100px'
            }}
          />
        ) : (
          <p style={{ whiteSpace: 'pre-wrap' }}>{data?.observaciones || '-'}</p>
        )}
      </div>
    </div>
  );
};

const ValidacionTab = ({ data, editing, onChange }) => {
  const handleMigradoChange = (checked) => {
    onChange('validacion', 'migrado', checked ? 'SI' : 'NO');
  };

  const isMigrado = data?.migrado === 'SI' || data?.migrado === 'Si' || data?.migrado === 'si';

  return (
    <div className="fields-grid">
      {/* Campo Migrado - Checkbox */}
      <div className="field-item">
        <label>Migrado</label>
        {editing ? (
          <div className="checkbox-field">
            <input 
              type="checkbox" 
              id="migrado-checkbox"
              checked={isMigrado}
              onChange={(e) => handleMigradoChange(e.target.checked)} 
              data-testid="input-migrado"
              className="migrado-checkbox"
            />
            <label htmlFor="migrado-checkbox" className="checkbox-label">
              {isMigrado ? 'Sí, está migrado' : 'No está migrado'}
            </label>
          </div>
        ) : (
          <div className="migrado-display">
            <span className={`migrado-badge ${isMigrado ? 'migrado-si' : 'migrado-no'}`}>
              {isMigrado ? 'SI' : 'NO'}
            </span>
          </div>
        )}
      </div>
      
      {/* Campo Revisado - Select especial */}
      <div className="field-item">
        <label>Revisado</label>
        {editing ? (
          <select 
            value={data?.revisado || 'NO'} 
            onChange={(e) => onChange('validacion', 'revisado', e.target.value)} 
            data-testid="input-revisado"
            className="revisado-select"
          >
            <option value="NO">NO</option>
            <option value="SI">SI</option>
          </select>
        ) : (
          <div className="revisado-display">
            <span className={`revisado-badge ${data?.revisado === 'SI' ? 'revisado-si' : 'revisado-no'}`}>
              {data?.revisado || 'NO'}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

const HistorialTab = ({ history }) => {
  if (!history || history.length === 0) {
    return <div className="empty-state"><p>No hay cambios registrados</p></div>;
  }

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('es-VE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getActionLabel = (action) => {
    const actionIcons = {
      'create': <FileText size={16} />,
      'update': <Edit3 size={16} />,
      'import_create': <FileText size={16} />,
      'import_update': <Repeat size={16} />
    };
    
    const labels = {
      'create': 'Expediente Creado',
      'update': 'Expediente Modificado',
      'import_create': 'Importado desde Excel',
      'import_update': 'Actualizado desde Excel'
    };
    
    return {
      icon: actionIcons[action] || <Edit3 size={16} />,
      label: labels[action] || action
    };
  };

  const renderChanges = (changes) => {
    if (!changes || Object.keys(changes).length === 0) return null;

    return (
      <div className="changes-details">
        <h5 className="changes-title">Cambios realizados:</h5>
        {Object.entries(changes).map(([group, groupChanges]) => (
          <div key={group} className="change-group">
            <h6 className="change-group-title">{group.charAt(0).toUpperCase() + group.slice(1)}:</h6>
            {typeof groupChanges === 'object' && Object.entries(groupChanges).map(([field, change]) => (
              <div key={field} className="change-item">
                <span className="change-field">{field}:</span>
                <span className="change-values">
                  <span className="old-value">&quot;{change.antes || 'vacío'}&quot;</span>
                  <span className="arrow">→</span>
                  <span className="new-value">&quot;{change.despues || 'vacío'}&quot;</span>
                </span>
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="historial-list">
      {history.map((log, idx) => {
        const actionInfo = getActionLabel(log.action);
        return (
          <div key={idx} className="historial-item">
            <div className="historial-header">
              <div className="historial-action">
                <span className="action-icon">{actionInfo.icon}</span>
                <span className="action-label">{actionInfo.label}</span>
              </div>
              <span className="historial-date">{formatDate(log.at)}</span>
            </div>
            <div className="historial-user">
              <span className="user-icon">👤</span>
              <span>Modificado por: <strong>{log.by_user_name || `Usuario ${log.by_user}`}</strong></span>
            </div>
            {log.diff && renderChanges(log.diff)}
          </div>
        );
      })}
    </div>
  );
};

// ============================================================================
// TAB DOCUMENTACIÓN - Integración con DocVault
// ============================================================================
const DocumentacionTab = ({ cedula }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [cedulaEncontrada, setCedulaEncontrada] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [currentDocument, setCurrentDocument] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  const DOCVAULT_API_URL = process.env.REACT_APP_DOCVAULT_URL || 'https://smart-audit-12.preview.emergentagent.com';

  useEffect(() => {
    if (cedula) {
      loadDocuments();
    }
  }, [cedula]);

  const loadDocuments = async () => {
    if (!cedula) {
      setError('No hay cédula asociada a este expediente');
      return;
    }

    setLoading(true);
    setError(null);
    setCedulaEncontrada(false);

    try {
      const response = await fetch(
        `https://expedientes.kompa.global/api/public/expedientes/buscar/cedula/${cedula}`,
        {
          headers: {
            'X-API-Key': 'c2ZFmIgazCaRA4Mz41SXdxhlJwjqCJDz4gUn33kiXyM'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Error al cargar documentos desde Gestor de Expedientes');
      }

      const expedientes = await response.json();
      
      // Si el array está vacío, la cédula no existe
      if (!expedientes || expedientes.length === 0) {
        // MOCK: Agregar PDF de prueba para cédula 19711598 (para testing del visor)
        if (cedula === '19711598') {
          const mockPDF = {
            id: 'mock_pdf_test',
            nombre: 'Mozilla_PDF_Prueba.pdf',
            tipo: 'PDF',
            fecha: new Date().toISOString(),
            tamano: 'PDF',
            url: 'https://mozilla.github.io/pdf.js/web/compressed.tracemonkey-pldi-09.pdf',
            resource_type: 'raw',
            expediente_nombre: 'Documento de Prueba'
          };
          setDocuments([mockPDF]);
          setCedulaEncontrada(true);
          return;
        }
        
        setCedulaEncontrada(false);
        setDocuments([]);
        return;
      }

      // Extraer todas las imágenes de todos los expedientes con esa cédula
      const todasLasImagenes = [];
      expedientes.forEach((exp, expIdx) => {
        if (exp.imagenes && exp.imagenes.length > 0) {
          exp.imagenes.forEach((img, imgIdx) => {
            // Extraer nombre del archivo del public_id
            const nombreArchivo = img.public_id.split('/').pop() || `documento_${imgIdx + 1}`;
            const extension = img.format ? img.format.toUpperCase() : 'Archivo';
            
            // Calcular tamaño aproximado (no viene en la API, usar placeholder)
            const tamano = img.resource_type === 'image' ? 'Imagen' : 'PDF';
            
            todasLasImagenes.push({
              id: `${exp.id}_${imgIdx}`,
              nombre: nombreArchivo,
              tipo: extension,
              fecha: img.uploaded_at || exp.fecha_digitalizacion,
              tamano: tamano,
              url: img.url,
              resource_type: img.resource_type,
              expediente_nombre: exp.nombre
            });
          });
        }
      });

      setDocuments(todasLasImagenes);
      setCedulaEncontrada(todasLasImagenes.length > 0);
      
    } catch (err) {
      console.error('Error cargando documentos:', err);
      setError('No se pudieron cargar los documentos. Verifica la conexión con el Gestor de Expedientes.');
      setCedulaEncontrada(false);
    } finally {
      setLoading(false);
    }
  };

  const openViewer = (doc, index) => {
    setCurrentDocument(doc);
    setCurrentIndex(index);
    setViewerOpen(true);
  };

  const closeViewer = () => {
    setViewerOpen(false);
    setCurrentDocument(null);
  };

  const navigateDocument = (direction) => {
    const newIndex = direction === 'next' 
      ? (currentIndex + 1) % documents.length 
      : (currentIndex - 1 + documents.length) % documents.length;
    
    setCurrentIndex(newIndex);
    setCurrentDocument(documents[newIndex]);
  };

  if (loading) {
    return (
      <div className="tab-content">
        <div className="loading-state" style={{ textAlign: 'center', padding: '40px' }}>
          <RefreshCw size={32} className="spinning" style={{ margin: '0 auto 16px', color: '#fbb040' }} />
          <p style={{ color: '#9ca3af' }}>Cargando documentos desde Gestor de Expedientes...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tab-content">
        <div className="error-state" style={{ 
          textAlign: 'center', 
          padding: '40px',
          background: 'rgba(239, 68, 68, 0.1)',
          borderRadius: '12px',
          border: '1px solid rgba(239, 68, 68, 0.3)'
        }}>
          <p style={{ color: '#ef4444', marginBottom: '16px' }}>{error}</p>
          <button 
            onClick={loadDocuments}
            style={{
              padding: '10px 20px',
              background: '#fbb040',
              border: 'none',
              borderRadius: '8px',
              color: '#111827',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  if (!cedula) {
    return (
      <div className="tab-content">
        <div className="empty-state" style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>
          <FolderOpen size={48} style={{ margin: '0 auto 16px', opacity: 0.5 }} />
          <p>Este expediente no tiene una cédula asociada</p>
        </div>
      </div>
    );
  }

  return (
    <div className="tab-content">
      {/* Indicador prominente - SI la cédula existe en Gestor de Expedientes o NO */}
      <div style={{
        padding: '16px 20px',
        marginBottom: '16px',
        borderRadius: '12px',
        border: '2px solid',
        borderColor: cedulaEncontrada ? '#10b981' : '#ef4444',
        background: cedulaEncontrada 
          ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(16, 185, 129, 0.05) 100%)'
          : 'linear-gradient(135deg, rgba(239, 68, 68, 0.15) 0%, rgba(239, 68, 68, 0.05) 100%)',
        display: 'flex',
        alignItems: 'center',
        gap: '12px'
      }}>
        <div style={{
          width: '12px',
          height: '12px',
          borderRadius: '50%',
          background: cedulaEncontrada ? '#10b981' : '#ef4444',
          boxShadow: cedulaEncontrada 
            ? '0 0 10px rgba(16, 185, 129, 0.5)' 
            : '0 0 10px rgba(239, 68, 68, 0.5)',
          animation: !cedulaEncontrada ? 'pulse 2s infinite' : 'none'
        }} />
        <div style={{ flex: 1 }}>
          <p style={{ 
            margin: 0, 
            fontSize: '14px', 
            fontWeight: '600',
            color: cedulaEncontrada ? '#10b981' : '#ef4444'
          }}>
            {cedulaEncontrada 
              ? `✓ Cédula ${cedula} encontrada en Gestor de Expedientes` 
              : `✗ Cédula ${cedula} no encontrada en Gestor de Expedientes`}
          </p>
          <p style={{ 
            margin: '2px 0 0 0', 
            fontSize: '12px', 
            color: '#9ca3af'
          }}>
            {cedulaEncontrada 
              ? `${documents.length} documento(s) disponible(s)` 
              : 'No hay documentos escaneados para este expediente'}
          </p>
        </div>
        <div style={{
          padding: '4px 12px',
          background: cedulaEncontrada ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)',
          border: `1px solid ${cedulaEncontrada ? 'rgba(16, 185, 129, 0.4)' : 'rgba(239, 68, 68, 0.4)'}`,
          borderRadius: '6px',
          fontSize: '11px',
          fontWeight: '600',
          color: cedulaEncontrada ? '#10b981' : '#ef4444',
          textTransform: 'uppercase',
          letterSpacing: '0.5px'
        }}>
          {cedulaEncontrada ? 'ENCONTRADA' : 'NO ENCONTRADA'}
        </div>
      </div>
      
      {/* Header simple con botón actualizar */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '24px',
        padding: '16px',
        background: 'rgba(31, 41, 55, 0.5)',
        borderRadius: '12px',
        border: '1px solid rgba(75, 85, 99, 0.3)'
      }}>
        <div>
          <h3 style={{ margin: '0 0 4px 0', color: 'white' }}>Documentos Escaneados</h3>
          <p style={{ margin: 0, fontSize: '14px', color: '#9ca3af' }}>
            Expediente con cédula: <strong style={{ color: '#fbb040' }}>{cedula}</strong>
          </p>
        </div>
        <button 
          onClick={loadDocuments}
          disabled={loading}
          style={{
            padding: '8px 16px',
            background: 'rgba(251, 176, 64, 0.2)',
            border: '1px solid rgba(251, 176, 64, 0.4)',
            borderRadius: '8px',
            color: '#fbb040',
            cursor: loading ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '14px',
            fontWeight: '500'
          }}
        >
          <RefreshCw size={16} className={loading ? 'spinning' : ''} />
          Actualizar
        </button>
      </div>

      {/* Lista de documentos */}
      {documents.length === 0 ? (
        <div className="empty-state" style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>
          <FolderOpen size={48} style={{ margin: '0 auto 16px', opacity: 0.5 }} />
          <p>No hay documentos asociados a esta cédula en el Gestor de Expedientes</p>
        </div>
      ) : (
        <div className="documents-grid" style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: '16px'
        }}>
          {documents.map((doc, index) => (
            <div 
              key={doc.id}
              style={{
                background: 'rgba(31, 41, 55, 0.5)',
                border: '1px solid rgba(75, 85, 99, 0.3)',
                borderRadius: '12px',
                padding: '16px',
                transition: 'all 0.2s',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(31, 41, 55, 0.7)';
                e.currentTarget.style.borderColor = '#fbb040';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(31, 41, 55, 0.5)';
                e.currentTarget.style.borderColor = 'rgba(75, 85, 99, 0.3)';
              }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  background: 'rgba(251, 176, 64, 0.2)',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <FileText size={24} style={{ color: '#fbb040' }} />
                </div>
                <div style={{ flex: 1 }}>
                  <h4 style={{ 
                    margin: '0 0 4px 0', 
                    color: 'white', 
                    fontSize: '14px',
                    fontWeight: '600',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {doc.nombre}
                  </h4>
                  <p style={{ margin: '0 0 8px 0', fontSize: '12px', color: '#9ca3af' }}>
                    {doc.tipo} • {doc.tamano}
                  </p>
                  <p style={{ margin: 0, fontSize: '12px', color: '#6b7280' }}>
                    {new Date(doc.fecha).toLocaleDateString('es-VE')}
                  </p>
                </div>
              </div>
              <div style={{ marginTop: '12px', display: 'flex', gap: '8px' }}>
                <button 
                  onClick={() => openViewer(doc, index)}
                  style={{
                    flex: 1,
                    padding: '8px',
                    background: '#fbb040',
                    border: 'none',
                    borderRadius: '6px',
                    color: '#111827',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Ver
                </button>
                <a 
                  href={doc.url}
                  download={doc.nombre}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    padding: '8px 12px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '6px',
                    color: 'white',
                    fontSize: '13px',
                    cursor: 'pointer',
                    textDecoration: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  Descargar
                </a>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal de visualización de documentos */}
      {viewerOpen && currentDocument && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.95)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}
          onClick={closeViewer}
        >
          <div 
            style={{
              position: 'relative',
              // Modal adaptativo: vertical para PDFs, flexible para imágenes
              width: currentDocument.resource_type === 'image' || currentDocument.tipo === 'JPG' || currentDocument.tipo === 'PNG'
                ? 'auto'
                : '80vw',  // PDFs: 80% del ancho de pantalla
              maxWidth: currentDocument.resource_type === 'image' || currentDocument.tipo === 'JPG' || currentDocument.tipo === 'PNG'
                ? '90vw'
                : '1000px',  // PDFs: máximo 1000px de ancho
              height: currentDocument.resource_type === 'image' || currentDocument.tipo === 'JPG' || currentDocument.tipo === 'PNG'
                ? 'auto'
                : '95vh',  // PDFs: 95% de la altura de pantalla
              maxHeight: '95vh',
              background: '#1f2937',
              borderRadius: '16px',
              overflow: 'hidden',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
              display: 'flex',
              flexDirection: 'column'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header del modal */}
            <div style={{
              padding: '16px 20px',
              background: 'rgba(31, 41, 55, 0.9)',
              borderBottom: '1px solid rgba(75, 85, 99, 0.3)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div>
                <h3 style={{ margin: '0 0 4px 0', color: 'white', fontSize: '16px' }}>
                  {currentDocument.nombre}
                </h3>
                <p style={{ margin: 0, fontSize: '13px', color: '#9ca3af' }}>
                  {currentDocument.tipo} • {new Date(currentDocument.fecha).toLocaleDateString('es-VE')} • Documento {currentIndex + 1} de {documents.length}
                </p>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <a 
                  href={currentDocument.url}
                  download={currentDocument.nombre}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    padding: '8px 16px',
                    background: '#fbb040',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#111827',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    textDecoration: 'none'
                  }}
                >
                  Descargar
                </a>
                <button 
                  onClick={closeViewer}
                  style={{
                    padding: '8px 16px',
                    background: 'rgba(239, 68, 68, 0.2)',
                    border: '1px solid rgba(239, 68, 68, 0.4)',
                    borderRadius: '8px',
                    color: '#ef4444',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Cerrar
                </button>
              </div>
            </div>

            {/* Contenido del documento */}
            <div style={{
              flex: 1,
              padding: '20px',
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'flex-start',
              background: '#0a0a0a'
            }}>
              {currentDocument.resource_type === 'image' || currentDocument.tipo === 'JPG' || currentDocument.tipo === 'PNG' ? (
                <img 
                  src={currentDocument.url}
                  alt={currentDocument.nombre}
                  style={{
                    maxWidth: '100%',
                    maxHeight: 'calc(90vh - 120px)',
                    objectFit: 'contain',
                    borderRadius: '8px'
                  }}
                />
              ) : currentDocument.resource_type === 'raw' || currentDocument.tipo === 'PDF' ? (
                <iframe 
                  src={`https://docs.google.com/viewer?url=${encodeURIComponent(currentDocument.url)}&embedded=true`}
                  title={currentDocument.nombre}
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none',
                    borderRadius: '8px',
                    background: 'white',
                    minHeight: '80vh'
                  }}
                />
              ) : (
                <div style={{ textAlign: 'center', padding: '60px', color: '#9ca3af' }}>
                  <p>Tipo de documento no soportado</p>
                </div>
              )}
            </div>

            {/* Navegación entre documentos */}
            {documents.length > 1 && (
              <>
                <button 
                  onClick={() => navigateDocument('prev')}
                  style={{
                    position: 'absolute',
                    left: '20px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: '48px',
                    height: '48px',
                    borderRadius: '50%',
                    background: 'rgba(251, 176, 64, 0.9)',
                    border: 'none',
                    color: '#111827',
                    fontSize: '24px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
                  }}
                >
                  ‹
                </button>
                <button 
                  onClick={() => navigateDocument('next')}
                  style={{
                    position: 'absolute',
                    right: '20px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: '48px',
                    height: '48px',
                    borderRadius: '50%',
                    background: 'rgba(251, 176, 64, 0.9)',
                    border: 'none',
                    color: '#111827',
                    fontSize: '24px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
                  }}
                >
                  ›
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpedienteDetalle;